import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { API_URLS } from '../../../config/api';
import { 
  Users, 
  UserCheck, 
  UserX, 
  Edit, 
  Trash2, 
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  Lock,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getUsers`);
      
      const responseText = await response.text();
      
      try {
        const result = JSON.parse(responseText);
        
        if (result.status === 'success') {
          setUsers(result.data || []);
        } else {
          setMessage('Kullanıcılar yüklenemedi');
          setMessageType('error');
        }
      } catch (parseError) {
        setMessage('Sunucudan geçersiz yanıt alındı');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };


  const handleApproveUser = async (userId, role = 'user') => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=approveUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Kullanıcı onaylandı');
        setMessageType('success');
        fetchUsers();
      } else {
        setMessage(result.message || 'Kullanıcı onaylanamadı');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=updateUserRole`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role: newRole })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Kullanıcı rolü güncellendi');
        setMessageType('success');
        fetchUsers();
      } else {
        setMessage(result.message || 'Rol güncellenemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=toggleUserStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Kullanıcı durumu güncellendi');
        setMessageType('success');
        fetchUsers();
      } else {
        setMessage(result.message || 'Durum güncellenemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=deleteUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setMessage('Kullanıcı silindi');
        setMessageType('success');
        fetchUsers();
      } else {
        setMessage(result.message || 'Kullanıcı silinemedi');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
      setMessageType('error');
    }
  };

  const handleOpenPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const generateStrongPassword = () => {
    const length = 16;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = uppercase + lowercase + numbers + symbols;
    
    // En az bir karakter her kategoriden olsun
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Kalan karakterleri rastgele ekle
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Karakterleri karıştır
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    return password;
  };

  const handleSuggestPassword = () => {
    const suggestedPassword = generateStrongPassword();
    setNewPassword(suggestedPassword);
    setConfirmPassword(suggestedPassword);
    setPasswordError('');
  };

  const handleUpdatePassword = async () => {
    setPasswordError('');
    
    if (!newPassword || !confirmPassword) {
      setPasswordError('Lütfen tüm alanları doldurun');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Şifreler eşleşmiyor');
      return;
    }

    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=updateUserPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: selectedUser.id,
          new_password: newPassword
        })
      });

      const responseText = await response.text();
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        setPasswordError('Sunucudan geçersiz yanıt alındı');
        setMessage('Sunucudan geçersiz yanıt alındı');
        setMessageType('error');
        return;
      }
      
      if (result.status === 'success') {
        setMessage('Kullanıcı şifresi başarıyla güncellendi');
        setMessageType('success');
        handleClosePasswordModal();
        fetchUsers();
      } else {
        const errorMsg = result.message || 'Şifre güncellenemedi';
        setPasswordError(errorMsg);
        setMessage(errorMsg);
        setMessageType('error');
      }
    } catch (error) {
      const errorMsg = 'Bağlantı hatası: ' + error.message;
      setPasswordError(errorMsg);
      setMessage(errorMsg);
      setMessageType('error');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'bg-red-100 text-red-800', icon: Shield, label: 'Admin' },
      editor: { color: 'bg-blue-100 text-blue-800', icon: Edit, label: 'Editör' },
      content_writer: { color: 'bg-green-100 text-green-800', icon: Users, label: 'İçerik Yazarı' }
    };
    
    const config = roleConfig[role] || roleConfig.content_writer;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (isApproved, isActive) => {
    if (!isActive) {
      return <Badge className="bg-gray-100 text-gray-800">Pasif</Badge>;
    }
    if (!isApproved) {
      return <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
        <Clock className="w-3 h-3" />
        Onay Bekliyor
      </Badge>;
    }
    return <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
      <CheckCircle className="w-3 h-3" />
      Aktif
    </Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Kullanıcılar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kullanıcı Yönetimi</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">Kullanıcıları onaylayın, rollerini düzenleyin ve site ziyaretçilerini görüntüleyin</p>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          <span className="text-xs sm:text-sm text-gray-600">
            {users.length} kullanıcı
          </span>
        </div>
      </div>


      {message && (
        <div className={`p-3 sm:p-4 rounded-md text-sm sm:text-base ${
          messageType === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg">Kayıtlı Kullanıcılar</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          {/* Mobile View - Card Layout */}
          <div className="block sm:hidden">
            <div className="space-y-3 p-4">
              {users.map((user) => (
                <div key={user.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">{user.full_name}</div>
                      <div className="text-xs text-gray-500 truncate">@{user.username}</div>
                    </div>
                    <div className="flex flex-col gap-1 ml-2">
                      {getRoleBadge(user.role)}
                      {getStatusBadge(user.is_approved, user.is_active)}
                    </div>
                  </div>
                  
                  <div className="space-y-1 text-xs">
                    <div className="text-gray-900 truncate">{user.email}</div>
                    {user.phone && (
                      <div className="text-gray-500 truncate">{user.phone}</div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 text-xs text-gray-500 pt-2 border-t border-gray-100">
                    <span className="truncate">Kayıt: {formatDate(user.created_at)}</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
                    {!user.is_approved && (
                      <Button
                        size="sm"
                        onClick={() => handleApproveUser(user.id, user.role)}
                        className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2"
                      >
                        <UserCheck className="w-3 h-3" />
                      </Button>
                    )}
                    
                    <select
                      value={user.role}
                      onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                      className="text-xs border rounded px-2 py-1 h-7 flex-1 min-w-[100px]"
                    >
                      <option value="content_writer">İçerik Yazarı</option>
                      <option value="editor">Editör</option>
                      <option value="admin">Admin</option>
                    </select>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleStatus(user.id)}
                      className={`h-7 px-2 ${user.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                    >
                      {user.is_active ? <UserX className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenPasswordModal(user)}
                      className="text-blue-600 hover:text-blue-700 h-7 px-2"
                      title="Şifre Değiştir"
                    >
                      <Lock className="w-3 h-3" />
                    </Button>

                    {user.role !== 'admin' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-700 h-7 px-2"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {users.length === 0 && (
              <div className="text-center py-8 text-gray-500 p-4">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">Henüz kullanıcı bulunmuyor</p>
              </div>
            )}
          </div>

          {/* Desktop View - Table Layout */}
          <div className="hidden sm:block">
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full min-w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-xs sm:text-sm">Kullanıcı</th>
                    <th className="text-left py-3 px-4 text-xs sm:text-sm">İletişim</th>
                    <th className="text-left py-3 px-4 text-xs sm:text-sm">Rol</th>
                    <th className="text-left py-3 px-4 text-xs sm:text-sm">Durum</th>
                    <th className="text-left py-3 px-4 text-xs sm:text-sm hidden lg:table-cell">Kayıt Tarihi</th>
                    <th className="text-left py-3 px-4 text-xs sm:text-sm hidden xl:table-cell">Son Giriş</th>
                    <th className="text-left py-3 px-4 text-xs sm:text-sm">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-sm text-gray-900">{user.full_name}</div>
                          <div className="text-xs text-gray-500">@{user.username}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <div className="text-xs sm:text-sm text-gray-900 truncate max-w-xs">{user.email}</div>
                          {user.phone && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">{user.phone}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(user.is_approved, user.is_active)}
                      </td>
                      <td className="py-3 px-4 text-xs sm:text-sm text-gray-600 hidden lg:table-cell">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="py-3 px-4 text-xs sm:text-sm text-gray-600 hidden xl:table-cell">
                        {formatDate(user.last_login)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                          {!user.is_approved && (
                            <Button
                              size="sm"
                              onClick={() => handleApproveUser(user.id, user.role)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <UserCheck className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          )}
                          
                          <select
                            value={user.role}
                            onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                            className="text-xs border rounded px-2 py-1"
                          >
                            <option value="content_writer">İçerik Yazarı</option>
                            <option value="editor">Editör</option>
                            <option value="admin">Admin</option>
                          </select>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleStatus(user.id)}
                            className={user.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                          >
                            {user.is_active ? <UserX className="w-3 h-3 sm:w-4 sm:h-4" /> : <UserCheck className="w-3 h-3 sm:w-4 sm:h-4" />}
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenPasswordModal(user)}
                            className="text-blue-600 hover:text-blue-700"
                            title="Şifre Değiştir"
                          >
                            <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>

                          {user.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {users.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Henüz kullanıcı bulunmuyor</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Password Change Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="w-[95vw] sm:max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Şifre Değiştir</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {selectedUser && `${selectedUser.full_name} (${selectedUser.email}) kullanıcısının şifresini değiştirin`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <Label htmlFor="newPassword" className="text-sm">Yeni Şifre</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSuggestPassword}
                  className="text-xs h-7 px-2 w-full sm:w-auto"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Şifre Öner
                </Button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 pr-10"
                  placeholder="Yeni şifre"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-8 w-8"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm">Yeni Şifre (Tekrar)</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10"
                  placeholder="Yeni şifre (tekrar)"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-8 w-8"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {passwordError && (
              <div className="text-xs sm:text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                {passwordError}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleClosePasswordModal}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              İptal
            </Button>
            <Button
              onClick={handleUpdatePassword}
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto order-1 sm:order-2"
            >
              Şifreyi Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;

